import {PriceResponse} from 'models/calculator';
import {countWords, formatPrice} from './common';
import {CLOUD_TITLE} from '../constants';
import {sanitizeHtml} from './sanitize';
import {Locale} from 'contexts/LocaleContext';
import {getYear} from '../../common/utils';
import {Product} from 'units/marketplace/models';
import {Currency} from 'models/billing';

export function getAuthorSchema(locale: Locale, author?: string, scale = false) {
    return author
        ? {
              '@type': 'Person',
              name: author,
          }
        : getOrganizationSchema(locale, scale);
}

export function getOrganizationSchema({tld}: Locale, scale = false) {
    const url = scale ? `https://scale.yandex.${tld}` : `https://cloud.yandex.${tld}`;
    const iconUrl = `${url}/favicon.png`;
    const name = scale ? `${CLOUD_TITLE} Scale` : CLOUD_TITLE;

    return {
        '@type': 'Organization',
        name,
        url,
        logo: {
            '@type': 'ImageObject',
            url: iconUrl,
            width: 32,
            height: 32,
        },
    };
}

export function getLanguageSchema({lang, langName}: Locale) {
    return {
        '@type': 'Language',
        name: langName,
        alternateName: lang,
    };
}

export function getSharedContentSchema(
    locale: Locale,
    url: string,
    title: string,
    author?: string,
    scale = false,
) {
    return {
        '@type': 'WebPage',
        headline: title,
        url: sanitizeHtml(url),
        author: getAuthorSchema(locale, author, scale),
    };
}

interface BlogPostData {
    url: string;
    title: string;
    date: string;
    locale: Locale;
    description?: string;
    author?: string;
    image?: string;
    keywords?: string[];
    content?: string;
}

export function getBlogPostSchema(data: BlogPostData, scale = false) {
    const {
        url,
        title,
        description,
        date,
        author: authorName,
        keywords,
        image,
        content,
        locale,
    } = data;

    const safeUrl = sanitizeHtml(url);
    const author = getAuthorSchema(locale, authorName, scale);
    const organization = getOrganizationSchema(locale, scale);
    const language = getLanguageSchema(locale);
    const sharedContent = getSharedContentSchema(locale, url, title, authorName, scale);

    return {
        '@context': 'http://schema.org/',
        '@type': 'BlogPosting',
        '@id': safeUrl,
        url: safeUrl,
        name: title,
        headline: title,
        abstract: description,
        description,
        dateCreated: date,
        datePublished: date,
        dateModified: date,
        author,
        creator: author,
        publisher: organization,
        copyrightHolder: organization,
        copyrightYear: getYear(date || new Date()),
        mainEntityOfPage: true,
        inLanguage: language,
        keywords,
        image,
        sharedContent,
        wordCount: content && countWords(content),
        articleBody: content,
    };
}

export interface BlogData {
    url: string;
    title: string;
    description: string;
    locale: Locale;
    blogPostsData?: BlogPostData[];
}

export function getBlogSchema(data: BlogData, scale = false) {
    const {url, title, description, locale, blogPostsData} = data;

    const safeUrl = sanitizeHtml(url);
    const organization = getOrganizationSchema(locale, scale);

    return {
        '@context': 'http://schema.org/',
        '@type': 'Blog',
        '@id': safeUrl,
        name: title,
        url: safeUrl,
        description,
        publisher: organization,
        blogPosts: blogPostsData
            ? blogPostsData.map((postData) => getBlogPostSchema(postData, scale))
            : [],
    };
}

export interface ProductData {
    url: string;
    locale: Locale;
    product: Product;
    minimumPrices: PriceResponse[];
}

export function getProductSchema(data: ProductData) {
    const {
        url,
        locale,
        product: {
            marketingInfo: {name, shortDescription, logo},
            publisher: {
                marketingInfo: {logo: publisherLogo, name: publisherName},
            },
        },
        minimumPrices,
    } = data;

    if (!minimumPrices) {
        return undefined;
    }

    const safeUrl = sanitizeHtml(url);

    const minimumPrice = minimumPrices.reduce((sum, {cost}) => sum + cost, 0);
    const price = formatPrice(minimumPrice, locale, {
        isInteger: locale.currency !== Currency.USD,
    });

    return {
        '@context': 'http://schema.org/',
        '@type': 'Product',
        '@id': safeUrl,
        name,
        url: safeUrl,
        description: shortDescription,
        image: logo,
        offers: {
            '@type': 'AggregateOffer',
            lowPrice: price,
            priceCurrency: locale.currency,
        },
        brand: {
            '@type': 'Organization',
            name: publisherName,
            logo: publisherLogo,
        },
    };
}
